import { MDSIcon, MDSIconColor, MDSIconKind, MDSLink, MDSSectionSlotName } from "@/mds";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { windowModule } from "@/modules/window";
import {
  StyledCardIntegrationInstruction,
  StyledContentList,
  StyledContentSectionTitleWrapper,
  StyledContentStatusWrapper,
} from "@/pages/integrations/shared/integrations.styles";
import { APP_CONFIG } from "@/domains/global/config";

interface IntegrationsZapierPageDescriptionProps {
  isConnected: boolean;
  onZapierIntegrationLinkClick: () => void;
}

export const IntegrationsZapierPageDescription: FC<IntegrationsZapierPageDescriptionProps> =
  observer(({ isConnected, onZapierIntegrationLinkClick }) => {
    return (
      <StyledCardIntegrationInstruction
        title={
          <StyledContentSectionTitleWrapper>
            <MDSIcon kind={MDSIconKind.LogoZapier} />
            <span>Zapier Integration</span>
          </StyledContentSectionTitleWrapper>
        }
      >
        <div slot={MDSSectionSlotName.HeaderRightAddon}>
          <StyledContentStatusWrapper>
            {isConnected ? "Connected" : "Not connected"}
            <MDSIcon
              kind={isConnected ? MDSIconKind.CheckCircleSolid : MDSIconKind.CircleSolid}
              color={isConnected ? MDSIconColor.Positive : MDSIconColor.Danger}
            />
          </StyledContentStatusWrapper>
        </div>
        <StyledContent>
          <StyledIntroText>
            <WebsiteLink onClick={onZapierIntegrationLinkClick}>Zapier</WebsiteLink> lets you
            connect Mem to 8,000+ apps without writing a single line of code.
          </StyledIntroText>
          <StyledSecondaryText>A few popular use-cases:</StyledSecondaryText>
          <StyledIconList>
            <li>
              <StyledListBoldText>
                <InlineMDSIcon kind={MDSIconKind.Microphone} size={18} /> Meeting Transcription
                Archive
              </StyledListBoldText>{" "}
              - Automatically import and format meeting transcripts from tools like Fireflies,
              Otter, and Fathom.
            </li>
            <li>
              <StyledListBoldText>
                <InlineMDSIcon kind={MDSIconKind.Comment} size={18} /> Slack Message Capture
              </StyledListBoldText>{" "}
              - Save important messages and conversations from Slack to Mem with a simple star or
              reaction.
            </li>
            <li>
              <StyledListBoldText>
                <InlineMDSIcon kind={MDSIconKind.Folder} size={18} /> Google Drive Sync
              </StyledListBoldText>{" "}
              - Bring documents from specific folders in Google Drive into Mem, ensuring your
              important work is always searchable alongside your notes.
            </li>
            <li>
              <StyledListBoldText>
                <InlineMDSIcon kind={MDSIconKind.ListCheck} size={18} /> Task Completion Journal
              </StyledListBoldText>{" "}
              - Track completed tasks from Todoist, Asana, or other task managers to maintain a
              searchable work log.
            </li>
            <li>
              <StyledListBoldText>
                <InlineMDSIcon kind={MDSIconKind.Clipboard} size={18} /> Form Response Collector
              </StyledListBoldText>{" "}
              - Capture submissions from Google Forms, Typeform, or other survey tools directly in
              Mem for organized feedback management.
            </li>
          </StyledIconList>
          <StyledVideoSection>
            <StyledVideoText>
              Automate Your Meeting Notes in Mem -{" "}
              <WebsiteLink
                onClick={() =>
                  windowModule.openInNewTab({
                    url: new URL(APP_CONFIG.URLS.ZAPIER_VIDEO_YOUTUBE_URL),
                  })
                }
              >
                Watch Video
              </WebsiteLink>
            </StyledVideoText>
            <ZapierVideo controls preload="auto">
              <source src={APP_CONFIG.URLS.ZAPIER_VIDEO_ASSET_URL} type="video/mp4" />
              Your browser does not support the video tag.
            </ZapierVideo>
          </StyledVideoSection>
        </StyledContent>
      </StyledCardIntegrationInstruction>
    );
  });

const StyledListBoldText = styled.span`
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StyledContent = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["text-m"]};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${({ theme }) => `0 ${theme.spacing[5]}`};
  max-width: 100%;
`;

const WebsiteLink = styled(MDSLink)(({ theme }) => ({
  fontWeight: theme.fontWeights.medium,
}));

const StyledIntroText = styled.div`
  line-height: ${({ theme }) => theme.lineHeights["header-l"]};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StyledSecondaryText = styled.div`
  padding-top: 14px;
  line-height: ${({ theme }) => theme.lineHeights["header-m"]};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InlineMDSIcon = styled(MDSIcon)`
  display: inline-block;
  margin-right: 1px;
  text-align: center;
`;

const StyledIconList = styled(StyledContentList)`
  list-style-type: none;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const StyledVideoSection = styled.div`
  padding-top: 6px;
`;

const StyledVideoText = styled.div`
  padding-top: 14px;
  line-height: ${({ theme }) => theme.lineHeights["header-m"]};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ZapierVideo = styled.video`
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding-top: ${({ theme }) => theme.spacing["2"]};
  margin-right: ${({ theme }) => theme.spacing["4"]};
  width: 50vw;
  max-width: 600px;
  min-width: 300px;
  height: 25vw;
  max-height: 300px;
  min-height: 150px;
`;
